import { useIntl } from 'react-intl';
import { ExtensionEvent } from '@rossum/api-client/hooks';
import { Chip, Stack, Tooltip, Typography } from '@rossum/ui/material';
import { convertEvents, getExtensionChipText } from '../../../../lib/helpers';
import { ExtensionText, ExtensionTextWithTooltip } from './ExtensionText';

type Props = {
  events: Array<ExtensionEvent>;
  id: string | number;
  displayPlaceholder?: boolean;
};

export const EventsBadges = ({
  events = [],
  id,
  displayPlaceholder,
}: Props) => {
  const intl = useIntl();

  if (events.length === 0) {
    return displayPlaceholder ? (
      <Typography
        variant="caption"
        color="text.disabled"
        sx={{ mx: 0.5, my: 1.5 }}
      >
        {intl.formatMessage({
          id: 'containers.settings.extensions.events.placeholder',
        })}
      </Typography>
    ) : null;
  }

  const convertedEvents = convertEvents(events);
  const eventValues = Object.values(convertedEvents);
  const [firstEvent, ...restOfEvents] = eventValues;

  const firstChip = firstEvent
    ? getExtensionChipText({
        event: firstEvent.name,
        actions: firstEvent.actions,
        intl,
      })
    : undefined;

  return (
    <Stack
      direction="row"
      alignItems="center"
      px={1}
      spacing={1}
      sx={{ maxWidth: 'calc(100% - 24px)' }}
    >
      {firstChip ? (
        <Chip
          label={
            <ExtensionTextWithTooltip
              secondaryText={firstChip.eventsText}
              primaryText={firstChip.actionsText}
            />
          }
          sx={{ minWidth: 0 }}
        />
      ) : null}
      {!!restOfEvents.length && (
        <Tooltip
          placement="top"
          disableInteractive
          title={restOfEvents.map(({ actions, name }) => {
            const {
              eventsText: tooltipEventsText,
              actionsText: tooltipActionsText,
            } = getExtensionChipText({
              event: name,
              actions,
              intl,
            });

            return (
              <ExtensionText
                key={`${name}-${id}`}
                primaryText={tooltipActionsText}
                secondaryText={tooltipEventsText}
              />
            );
          })}
        >
          <Chip label={`+${restOfEvents.length}`} />
        </Tooltip>
      )}
    </Stack>
  );
};
